// import { Tooltip } from 'antd';
import { Tooltip } from 'antd';
import * as echarts from 'echarts/core';
import moment from 'moment';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { getPrice } from '../../lib';
import { ECOption } from '../../model';
import { ChartEmpty } from './empty';

interface IProps {
  className?: string;
  style?: CSSProperties;
  stack?: boolean;
  legend: ReadonlyArray<string>;
  xAxisLabel: ReadonlyArray<string>;
  seriesData: ReadonlyArray<Array<number>>;
  isHover?: boolean;
  isSelect?: boolean;
  onSelect?: (value: string) => void;
}

export const ChartBar = (props: IProps) => {
  const chartContainer = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const {
    className,
    stack,
    legend,
    style,
    xAxisLabel,
    seriesData,
    onSelect,
    isHover,
    isSelect,
  } = props;

  useEffect(() => {
    // 默认选中当月
    if (xAxisLabel.length > 0 && selectedIndex === null) {
      let selectMonth;
      const currentMonth = moment().format('YYYY-MM');
      if (xAxisLabel.length === 1) {
        selectMonth = moment(xAxisLabel[0]).format('YYYY-MM');
      } else {
        selectMonth = currentMonth;
      }
      const initialIndex = xAxisLabel.indexOf(selectMonth);
      setSelectedIndex(initialIndex !== -1 ? initialIndex : null);
      if (onSelect) {
        onSelect(selectMonth);
      }
    } else {
      if (onSelect) {
        onSelect('');
      }
    }
  }, [xAxisLabel]);
  useEffect(() => {
    if (chartContainer.current) {
      chartInstance.current = echarts.init(chartContainer.current);
      const formatYAxisLabel = (value: number) => {
        if (value >= 10000) {
          return `${(value / 10000).toFixed(0)}w`;
        }
        if (value >= 1000) {
          return `${(value / 1000).toFixed(0)}k`;
        }
        return value.toString();
      };

      const option: ECOption = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(247, 248, 250, 0.4)', // 设置阴影颜色
              zIndex: -1, // 设置为较低的 zIndex
            },
          },
          formatter: (params: any) => {
            if (params.length > 0 && params[0].axisValue) {
              const xAxisValue = params[0].axisValue;
              const xAxisValueWithColor = `<span style="color: #1D2129">${xAxisValue}</span>`;
              let tooltipContent = `${xAxisValueWithColor}<br/>`;
              params.forEach((param: any) => {
                const { seriesName, value } = param;
                const { color } = param.color.colorStops[0];
                const formattedValue = `${
                  value ? `${getPrice(value).text}` : '¥0.00'
                }`;
                const marker = `<span style="display:inline-block;margin-right:4px;width:8px;height:8px;border-radius:2px;background-color:${color}"></span>`;
                tooltipContent += `<div style="display: flex; align-items: center;">${marker} ${seriesName}<span style="flex-grow: 1; text-align: right;margin-left:35px;">${formattedValue}</span></div>`;
              });
              tooltipContent = `<div style="line-height: 22px">${tooltipContent}</div>`;
              return tooltipContent;
            }
            if (params.length > 0 && params[0].componentType === 'xAxis') {
              // 如果hoverX 轴标签
              return '点击查看费用详情';
            }
            return '';
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'MonaSans-Regular',
          },
          extraCssText: 'padding: 12px; border-radius: 8px;',
        },
        legend: {
          data: legend,
          left: 'auto',
          top: 0,
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 16,
          textStyle: {
            color: '#4E5969', // 设置图例文本颜色
            fontSize: 12,
          },
        },
        xAxis: {
          triggerEvent: true,
          data: [...xAxisLabel],
          axisLine: {
            show: false, // 显示X轴线
            lineStyle: {
              color: '#C9CDD4', // 轴线颜色
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0, // 避免标签显示不完全！
            show: true,
            color: '#4E5969',
            fontSize: 12,
            backgroundColor: '#fff',
            fontWeight: 400,
            lineHeight: 23,
            borderWidth: 1,
            borderColor: '#C9CDD4',
            borderRadius: 4,
            height: 23,
            width: 72,
            formatter: (value: string) => {
              if (
                selectedIndex !== null &&
                xAxisLabel[selectedIndex] === value
              ) {
                return `{selected|${value}}`; // 选中
              }
              if (hoverIndex !== null && xAxisLabel[hoverIndex] === value) {
                return `{hover|${value}}`; // hover
              }
              return value; // 默认
            },
            rich: {
              selected: {
                backgroundColor: '#F2F3F5',
                color: '#5764F1',
                fontWeight: 500,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: '#5764F1',
                height: 23,
                width: 72,
              },
              hover: {
                color: '#5764F1', // hover 状态的颜色
                fontWeight: 500,
                backgroundColor: '#fff',
                borderColor: '#5764F1',
                borderWidth: 1,
                borderRadius: 4,
                height: 23,
                width: 72,
              },
            },
          },
          axisPointer: {
            // type: 'line',
            lineStyle: {
              // hover上去中轴线line- shadow只能二选一
              type: 'dashed',
              color: '#C9CDD4',
            },
          },
        },
        yAxis: {
          splitLine: {
            show: true, // 设置为false，不展示背景的横线
          },
          //   axisPointer: {
          //     type: 'line', // 使用线条作为中轴线
          //     lineStyle: {
          //         color: 'red', // 中轴线颜色
          //         width: 2, // 中轴线宽度
          //     },
          //  },
          axisLabel: {
            // color: '#86909C', // 设置Y轴文本颜色为#86909C
            formatter: formatYAxisLabel,
          },
          splitNumber: 4,
        },
        grid: {
          left: 0,
          right: 0,
          top: 50,
          bottom: 30,
          containLabel: true,
          show: false, // 不展示图表背景
        },
        series: seriesData.map((series, index) => {
          return {
            data: series,
            type: 'bar',
            z: 10,
            cursor: 'default',
            name: legend[index],
            stack: stack ? 'x' : undefined,
            barWidth: 32, // 最大宽度为32px
            barMinWidth: 8,
            itemStyle:
              index === 0
                ? {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 1, color: '#5764F1' },
                      { offset: 0, color: '#8F9FF7' },
                    ]),
                    borderRadius: 4,
                  }
                : {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 1, color: '#FAAC7B' },
                      { offset: 0, color: '#F77234' },
                    ]),
                    borderRadius: 4,
                  },
          };
        }),
      };

      chartInstance.current.setOption(option);

      // 更新图表配置项的函数
      const updateChartOptions = (
        selectedIndex: number | null,
        hoverIndex: number | null
      ) => {
        const updatedOption = {
          xAxis: {
            data: xAxisLabel,
            axisLabel: {
              formatter: (value: string) => {
                if (
                  selectedIndex !== null &&
                  xAxisLabel[selectedIndex] === value
                ) {
                  return `{selected|${value}}`;
                }
                if (hoverIndex !== null && xAxisLabel[hoverIndex] === value) {
                  return `{hover|${value}}`;
                }
                return value; // 默认状态
              },
            },
          },
        };

        chartInstance.current?.setOption(updatedOption);
      };

      // 绑定点击事件
      if (isSelect) {
        chartInstance.current.on('click', (params: any) => {
          if (params.componentType === 'xAxis' && params) {
            // console.log('点击的索引:', params.dataIndex,selectedIndex);
            if (onSelect) {
              setSelectedIndex(params.dataIndex);
              onSelect(params.value);
              updateChartOptions(params.dataIndex, null);
            }
          }
        });
      }
      // 绑定 hover 事件
      if (isHover) {
        chartInstance.current.on('mouseover', (params) => {
          if (params.componentType === 'xAxis' && params) {
            // console.log('params.dataIndex-hover:',params.dataIndex);
            setHoverIndex(params.dataIndex);
            updateChartOptions(selectedIndex, params.dataIndex);
          }
        });

        chartInstance.current.on('mouseout', (params) => {
          if (params.componentType === 'xAxis' && params) {
            setHoverIndex(null);
            // 更新 hover 状态，不影响选中状态
            updateChartOptions(selectedIndex, null);
          }
        });
      }

      // 监听窗口大小变化
      const handleResize = () => {
        chartInstance.current?.resize();
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartInstance.current) {
          chartInstance.current.dispose();
        }
      };
    }
  }, [xAxisLabel, seriesData, onSelect]);

  if (
    !seriesData.length ||
    seriesData.every((series) => !series || series.length === 0)
  ) {
    return <ChartEmpty />;
  }

  return (
    <div style={style} ref={chartContainer} className={className}>
      {xAxisLabel.map((label, index) => (
        <Tooltip
          title="点击查看费用详情"
          key={index}
          overlayClassName="edit-member-tooltip"
        >
          <div>{label}</div>
        </Tooltip>
      ))}
    </div>
  );
};
